import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { Users, Home, AlertCircle } from 'lucide-react';
import DashboardHeader from './components/DashboardHeader';
import StatCard from './components/StatCard';
import TabNavigation from './components/TabNavigation';
import OverviewTab from './components/OverviewTab';
import AnalysisTab from './components/AnalysisTab';
import AboutTab from './components/AboutTab';
import DetailsCard from './components/DetailsCard';
import Footer from './components/Footer';
import { APIS, ArcGISMaps, dataSources, projectInfo } from './data/constants';

function App() {
  const [activeTab, setActiveTab] = useState('overview');
  const [weatherData, setWeatherData] = useState([]);
  const [downtownData, setDowntownData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [latestCounts, setLatestCounts] = useState({
    totalHomelessCount: '0',
    homelessChange: '0',
    getItDoneCount: '0',
    mostCommonService: '',
    evictionNotices: '0',
    evictionChange: '0',
    mostCommonEvictionReason: '',
    avgRent: '0',
    rentChange: '0',
  });
  const [expandedCard, setExpandedCard] = useState(null);

  const [homelessDetails, setHomelessDetails] = useState(null);
  const [rentDetails, setRentDetails] = useState(null);
  const [evictionData, setEvictionData] = useState(null);

  // Analysis data states
  const [rfModelData, setRFModelData] = useState([]);
  const [forecastData, setForecastData] = useState([]);
  const [housingDecompData, setHousingDecompData] = useState([]);
  const [unempDecompData, setUnempDecompData] = useState([]);

  const getCurrentReportsCount = (reports) => {
    // Get reports from last 48 hours
    const last48Hours = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return reports.filter(report => 
      new Date(report.date_requested) >= last48Hours
    ).length;
  };
  
  const getMostCommonServiceType = (reports) => {
    if (!reports.length) return 'None (0%)';
    
    const counts = reports.reduce((acc, report) => {
      acc[report.service_name] = (acc[report.service_name] || 0) + 1;
      return acc;
    }, {});
    
    const sortedServices = Object.entries(counts)
      .sort((a, b) => b[1] - a[1]);
    
    const topService = sortedServices[0];
    const percentage = ((topService[1] / reports.length) * 100).toFixed(1);
    
    return `${topService[0]} (${percentage}%)`;
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Helper function to convert Celsius to Fahrenheit
        const convertCelsiusToFahrenheit = (celsius) => {
          return Math.round((celsius * 9/5) + 32);
        };

        // Fetch weather data
        const weatherRes = await axios.get(APIS.WEATHER);
        const processedWeather = weatherRes.data.daily.time.slice(0, 5).map((date, i) => ({
          date: new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          min: convertCelsiusToFahrenheit(weatherRes.data.daily.temperature_2m_min[i]),
          max: convertCelsiusToFahrenheit(weatherRes.data.daily.temperature_2m_max[i]),
          precip: weatherRes.data.daily.precipitation_probability_max[i]
        }));
        setWeatherData(processedWeather);

        // Fetch Get It Done Reports
        const reportsRes = await fetch('/api/get-it-done-reports');
        const reportsData = await reportsRes.json();
        setIncidents(reportsData);

        // Fetch CSV files
        const fetchCSV = async (filename) => {
          const response = await fetch(`/${filename}`);
          const text = await response.text();
          return new Promise((resolve) => {
            Papa.parse(text, {
              header: true,
              complete: (results) => resolve(results.data)
            });
          });
        };

        // Fetch and process all data files and APIs
        const [
          downtownResults,
          yearlyResults,
          rfResults,
          forecastResults,
          housingDecompResults,
          unempDecompResults
        ] = await Promise.all([
          fetchCSV('Downtown Homeless Data.csv'),
          fetchCSV('datasets combined.csv'),
          fetchCSV('R Studio data - predict RF model.csv'),
          fetchCSV('R Studio data - forecast (med house unemp).csv'),
          fetchCSV('R Studio data - decomp and forecast Med housing.csv'),
          fetchCSV('R Studio data - decomp and forecast unemp.csv')
        ]);

        setDowntownData(downtownResults.filter(row => row.date));
        setYearlyData(yearlyResults.filter(row => row.year));
        setRFModelData(rfResults.filter(row => row.date));
        setForecastData(forecastResults.filter(row => row.date));
        setHousingDecompData(housingDecompResults.filter(row => row.date));
        setUnempDecompData(unempDecompResults.filter(row => row.date));

        // Eviction Data from the report
        const evictionData = {
          unlawfulDetainerCases: [
            { year: 2017, count: 3450 },
            { year: 2018, count: 3790 },
            { year: 2019, count: 3910 },
            { year: 2020, count: 1480 },
            { year: 2021, count: 1220 },
            { year: 2022, count: 2290 },
          ],
          sheriffLockouts: [
            { year: 2017, count: 1770 },
            { year: 2018, count: 1490 },
            { year: 2019, count: 1570 },
            { year: 2020, count: 430 },
            { year: 2021, count: 530 },
            { year: 2022, count: 580 },
          ],
        };

        const evictionCurrentYear = 2022;
        const evictionPreviousYear = 2021;

        const evictionCurrentData = evictionData.unlawfulDetainerCases.find(e => e.year === evictionCurrentYear);
        const evictionPreviousData = evictionData.unlawfulDetainerCases.find(e => e.year === evictionPreviousYear);

        const evictionCurrentCount = evictionCurrentData.count;
        const evictionPreviousCount = evictionPreviousData.count;

        const evictionChange = ((evictionCurrentCount - evictionPreviousCount) / evictionPreviousCount) * 100;

        // Process Homeless Data
        const homelessData = {
          totalHomelessCount: 10605,
          homelessChange: 3,
          unshelteredCount: 6110,
          shelteredCount: 4495,
          unshelteredChange: 18,
          shelteredChange: -12,
          demographics: {
            veterans: { unshelteredPercent: 9, unshelteredCount: 544, shelteredPercent: 7, shelteredCount: 321 },
            females: { unshelteredPercent: 28, unshelteredCount: 661, shelteredPercent: 41, shelteredCount: 1858 },
            families: { unshelteredPercent: 2, unshelteredCount: 142, shelteredPercent: 32, shelteredCount: 1421 },
            youth: { unshelteredPercent: 8, unshelteredCount: 465, shelteredPercent: 30, shelteredCount: 1330 },
            chronicallyHomeless: { unshelteredPercent: 44, unshelteredCount: 2686, shelteredPercent: 24, shelteredCount: 1072 },
          },
          regions: [
            { name: 'City of San Diego', percent: 64, count: 6783 },
            { name: 'North County Inland', percent: 8.6, count: 876 },
            { name: 'North County Coastal', percent: 7.8, count: 865 },
            { name: 'South County', percent: 8, count: 849 },
            { name: 'East County', percent: 11.6, count: 1232 },
          ],
          source: 'San Diego CoC Region 2024 Point-in-Time Count Data',
          sourceUrl: 'https://www.rtfhsd.org/wp-content/uploads/2024/09/2024-San-Diego-Regional-Breakdown.pdf'
        };
        setHomelessDetails(homelessData);

        // Process Rent Data
        const rentData = {
          medianRent: 2950,
          percentageChange: 2,
          pricePerSqFt: 3.72,
          totalRentals: 2236,
          nationalAverageDifference: 48,
          asOfDate: 'December 2024',
          bedrooms: [
            { type: 'Studio', averageRent: 2197, last30Days: 4, lastYear: 8 },
            { type: '1 Bedroom', averageRent: 2470, last30Days: 6, lastYear: 2 },
            { type: '2 Bedroom', averageRent: 3443, last30Days: 8, lastYear: 3 },
            { type: '3 Bedroom', averageRent: 4598, last30Days: 10, lastYear: -2 },
            { type: '4 Bedroom', averageRent: 7740, last30Days: 13, lastYear: -2 },
          ],
          source: 'https://www.zumper.com/rent-research/san-diego-ca',
        };
        setRentDetails(rentData);

        setLatestCounts({
          totalHomelessCount: homelessData.totalHomelessCount.toLocaleString(),
          homelessChange: homelessData.homelessChange.toFixed(1),
          getItDoneCount: getCurrentReportsCount(reportsData).toString(),
          mostCommonService: getMostCommonServiceType(reportsData),
          evictionNotices: evictionCurrentCount.toLocaleString(),
          evictionChange: evictionChange.toFixed(1),
          mostCommonEvictionReason: 'Non-Payment',
          avgRent: `$${rentData.medianRent.toLocaleString()}`,
          rentChange: rentData.percentageChange.toFixed(1),
        });

        setEvictionData(evictionData);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'about', label: 'About' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <DashboardHeader />
      
      <main className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            title="Total Homeless Persons" 
            value={latestCounts.totalHomelessCount}
            change={parseFloat(latestCounts.homelessChange)}
            icon={Users}
            loading={loading}
            description="As of 2024"
            isExpanded={expandedCard === 'homelessness'}
            onToggle={() => setExpandedCard(expandedCard === 'homelessness' ? null : 'homelessness')}
          />
          <StatCard 
            title="Get It Done Reports (<48 H)" 
            value={latestCounts.getItDoneCount}
            icon={AlertCircle}
            loading={loading}
            description={<span>Most common: <span className="text-red-600">{latestCounts.mostCommonService}</span></span>}
            isExpanded={expandedCard === 'getitdone'}
            onToggle={() => setExpandedCard(expandedCard === 'getitdone' ? null : 'getitdone')}
          />
          <StatCard 
            title="Eviction Trends" 
            value={latestCounts.evictionNotices}
            change={parseFloat(latestCounts.evictionChange)}
            icon={AlertCircle}
            loading={loading}
            description={`Most common reason: ${latestCounts.mostCommonEvictionReason}`}
            isExpanded={expandedCard === 'evictions'}
            onToggle={() => setExpandedCard(expandedCard === 'evictions' ? null : 'evictions')}
          />
          <StatCard 
            title="Median Rent" 
            value={latestCounts.avgRent}
            change={parseFloat(latestCounts.rentChange)}
            icon={Home}
            loading={loading}
            description="As of December 2024"
            isExpanded={expandedCard === 'averageRent'}
            onToggle={() => setExpandedCard(expandedCard === 'averageRent' ? null : 'averageRent')}
          />
        </div>

        {expandedCard && (
          <div className="mb-8">
            <DetailsCard 
              expandedCard={expandedCard} 
              data={{
                incidents,
                evictionData,
                homelessDetails,
                rentDetails,
              }}
            />
          </div>
        )}

        <div className="border-b border-gray-200 mb-8">
          <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} />
        </div>

        <div className="space-y-6">
          {activeTab === 'overview' && (
            <OverviewTab 
              weatherData={weatherData}
              downtownData={downtownData}
              yearlyData={yearlyData}
              ArcGISMaps={ArcGISMaps}
            />
          )}

          {activeTab === 'analysis' && (
            <AnalysisTab 
              rfData={rfModelData}
              forecastData={forecastData}
              housingDecompData={housingDecompData}
              unempDecompData={unempDecompData}
            />
          )}

          {activeTab === 'about' && (
            <AboutTab projectInfo={projectInfo} dataSources={dataSources} />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default App;
